/**
 * OS-level desktop notifications via the Web Notifications API.
 *
 * Layered on TOP of the in-app sonner toast — fires only when:
 *  - The browser supports `Notification`
 *  - Permission has been granted
 *  - The Myzow tab is NOT currently focused (`document.hidden === true`)
 *
 * That last rule prevents double-notifying the user when they're actively
 * looking at the app: they already see the sonner toast and hear the ding.
 *
 * Permission must be requested from inside a user gesture (Chrome/Firefox
 * enforce this). The provider wires the bell-toggle click in
 * DashboardLayout to call `requestPermission()` exactly once when the
 * current status is 'default'.
 */

export const getPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

export const requestPermission = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  try {
    // Both promise- and callback-based APIs exist; promise form is current.
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
};

/**
 * Fire a system notification. Best-effort — silent failure if browser
 * refuses (e.g. permission revoked between polls).
 *
 * @param {object} opts
 * @param {string} opts.title    — top line of the notification
 * @param {string} opts.body     — supporting text
 * @param {function} opts.onClick — invoked when the user clicks the notification
 * @param {string} opts.tag       — coalesces repeat notifications (same tag replaces previous)
 * @param {boolean} opts.silent   — suppress OS sound (we mirror the in-app mute toggle here)
 */
export const showDesktopNotification = ({ title, body, onClick, tag = 'myzow-inbox', silent = false }) => {
  if (getPermission() !== 'granted') return null;
  // Only fire when the tab is hidden — otherwise the in-app toast is enough.
  if (typeof document !== 'undefined' && !document.hidden) return null;
  try {
    const n = new Notification(title, { body, tag, silent, icon: '/favicon.png' });
    n.onclick = () => {
      try { window.focus(); } catch { /* ignore */ }
      try { n.close(); } catch { /* ignore */ }
      if (typeof onClick === 'function') onClick();
    };
    return n;
  } catch {
    return null;
  }
};
